/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense, useCallback, useRef } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { jsx } from '@atlaskit/css';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type {
	GicConfigurationProps,
	IssueCreateEmbedSuccessPayload,
} from '@atlassian/gic-anywhere';

import { AsyncIntlProvider } from '../../../common';

import { useGicIssueCreateAnalytics } from './hooks/useGicIssueCreateAnalytics';

const styles = cssMap({
	issueCreateWrapper: {
		// This value is in sync with:
		// - platform/packages/linking-platform/smart-card/src/view/EmbedModal/components/embed-content/index.tsx
		// - platform/packages/design-system/modal-dialog/src/internal/components/positioner.tsx
		height: 'calc(100vh - 208px)', // greater or equal 60+60(modal padding) + 70(header height)
		display: 'grid',
	},
});

const messages = defineMessages({
	title: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.create-title',
		defaultMessage: 'Create work item',
		description: 'Title of the Jira issue create modal',
	},
	successFlagTitle: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.success-flag-title',
		defaultMessage: 'Created {issueKey}',
		description: 'Success flag title after a Jira issue is created.',
	},
	openInJira: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.open-in-jira',
		defaultMessage: 'Open in Jira',
		description: 'Action link text to open the created issue in Jira',
	},
	copyLink: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.copy-link',
		defaultMessage: 'Copy link',
		description: 'Action button text to copy the issue link to clipboard',
	},
	workItemErrorFlagTitle: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.work-item-error-flag-title',
		defaultMessage: "We couldn't create your work item",
		description: 'Error flag title when work item creation fails',
	},
	workItemErrorFlagDescription: {
		id: 'platform.navigation-experiences.universal-create-integrations.issue.work-item-error-flag-description',
		defaultMessage: 'There was an error while creating your work item. Please try again.',
		description: 'Error flag description when work item creation fails',
	},
});

const GicAnywhere = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/universal-create-integrations_gic-issue-create" */ '@atlassian/gic-anywhere'
	).then((module) => ({ default: module.GicAnywhere })),
);

export interface GicIssueCreateModalProps {
	/**
	 * Cloud ID of the site in the creation context.
	 * Required to configure the GIC embed with the correct site.
	 */
	cloudId: string;

	/**
	 * Name of the consumer application.
	 * Used for analytics and tracking purposes.
	 * @default 'universalCreate'
	 */
	consumer?: string;

	/**
	 * Whether the create is happening in a cross-app context.
	 */
	isCrossAppCreate?: boolean;

	/**
	 * Whether the modal is open.
	 */
	isOpen: boolean;

	/**
	 * Callback fired when the modal is closed.
	 * @param isCreateSuccess - Whether an issue was successfully created before closing
	 */
	onClose: (isCreateSuccess?: boolean) => void;

	/**
	 * Callback fired when issue creation fails.
	 * @param error - The error that occurred during issue creation
	 */
	onFailure?: (error: Error | unknown) => void;

	/**
	 * Callback fired when the GIC embed is initialized.
	 */
	onInit?: () => void;

	/**
	 * Callback fired when issue creation is successful.
	 * @param payload - Details about the created issue
	 */
	onSuccess?: (payload: IssueCreateEmbedSuccessPayload) => void;

	/**
	 * The product from which the object was created
	 */
	product?: string;

	/**
	 * The sub-product from which the object was created
	 */
	subProduct?: string;
}

/**
 * Default configuration for GIC that hides the site picker.
 * This is used when displaySitePicker should be false as per the ticket requirement.
 */
const DEFAULT_GIC_CONFIGURATION: GicConfigurationProps = {
	displaySitePicker: false,
};

export function GicIssueCreateModalInner({
	cloudId,
	consumer = 'universalCreate',
	product,
	subProduct,
	isCrossAppCreate,
	isOpen,
	onClose,
	onFailure,
	onInit,
	onSuccess,
}: GicIssueCreateModalProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const flagCounterRef = useRef(0);

	const {
		fireCloseButtonAnalytics,
		fireCopyLinkClickAnalytics,
		fireCreateSuccessAnalytics,
		fireCreateButtonAnalytics,
		fireOpenInJiraClickAnalytics,
	} = useGicIssueCreateAnalytics({
		product,
		subProduct,
		isCrossAppCreate,
	});

	const showSuccessFlag = useCallback(
		(payload: IssueCreateEmbedSuccessPayload) => {
			showFlag({
				id: `gic-issue-create-success-${(flagCounterRef.current += 1)}`,
				icon: <SuccessIcon color={token('color.icon.success')} label="" spacing="spacious" />,
				title: formatMessage(messages.successFlagTitle, {
					issueKey: payload.issueKey,
				}),
				isAutoDismiss: true,
				actions: [
					{
						content: (
							<Inline space="space.050" alignBlock="center">
								{formatMessage(messages.openInJira)}{' '}
								<LinkExternalIcon label="" color="currentColor" />
							</Inline>
						),
						href: payload.link,
						onClick: () => {
							fireOpenInJiraClickAnalytics();
						},
					},
					{
						content: formatMessage(messages.copyLink),
						onClick: () => {
							fireCopyLinkClickAnalytics();
							navigator.clipboard.writeText(payload.link).catch(() => {
								// Silently fail if clipboard API is unavailable
							});
						},
					},
				],
			});
		},
		[showFlag, formatMessage, fireCopyLinkClickAnalytics, fireOpenInJiraClickAnalytics],
	);

	const handleInit = useCallback(() => {
		onInit?.();
	}, [onInit]);

	const handleSuccess = useCallback(
		(payload: IssueCreateEmbedSuccessPayload) => {
			fireCreateButtonAnalytics();
			showSuccessFlag(payload);
			onSuccess?.(payload);
		},
		[fireCreateButtonAnalytics, onSuccess, showSuccessFlag],
	);

	const handleClose = useCallback(
		(isCreateSuccess?: boolean) => {
			onClose(isCreateSuccess);
			if (isCreateSuccess) {
				fireCreateSuccessAnalytics();
			} else {
				fireCloseButtonAnalytics();
			}
		},
		[fireCloseButtonAnalytics, fireCreateSuccessAnalytics, onClose],
	);

	const handleFailure = useCallback(
		(error: Error | unknown) => {
			showFlag({
				id: `gic-issue-create-error-${(flagCounterRef.current += 1)}`,
				icon: <StatusErrorIcon label="" color={token('color.icon.danger')} spacing="spacious" />,
				title: formatMessage(messages.workItemErrorFlagTitle),
				description: formatMessage(messages.workItemErrorFlagDescription),
				isAutoDismiss: true,
			});
			onFailure?.(error);
		},
		[onFailure, showFlag, formatMessage],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={() => handleClose()} width="large" shouldScrollInViewport>
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<div css={styles.issueCreateWrapper}>
							<Suspense fallback={null}>
								<GicAnywhere
									cloudId={cloudId}
									consumer={consumer}
									summary=""
									configuration={DEFAULT_GIC_CONFIGURATION}
									onInit={handleInit}
									onClose={handleClose}
									onSuccess={handleSuccess}
									onFailure={handleFailure}
									isPayloadReady
								/>
							</Suspense>
						</div>
					</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
}

/**
 * Modal component for creating Jira issues using GicAnywhere.
 *
 * This component wraps the lazy-loaded GicAnywhere component in a modal dialog.
 * It configures the GIC embed with displaySitePicker: false and uses the cloudId
 * from the CreationContext.
 *
 * On successful issue creation, a success flag is shown with:
 * - A green checkmark icon
 * - "Created {issueType} {issueKey}: {summary}" title
 * - "Open in Jira" action to open the issue in the current tab
 * - "Copy link" action to copy the issue URL to clipboard
 *
 * @example
 * ```tsx
 * import { GicIssueCreateModal } from '@atlassian/universal-create-integrations/gic-issue-create';
 *
 * function MyComponent() {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <GicIssueCreateModal
 *       cloudId="your-cloud-id"
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *       onSuccess={(payload) => console.log('Issue created:', payload)}
 *     />
 *   );
 * }
 * ```
 */
export function GicIssueCreateModal(props: GicIssueCreateModalProps): React.JSX.Element {
	return (
		<AsyncIntlProvider>
			<FlagsProvider>
				<GicIssueCreateModalInner {...props} />
			</FlagsProvider>
		</AsyncIntlProvider>
	);
}
